import { useState, useEffect } from 'react';
import { useApp, ROLES } from '../../context/AppContext';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  IdCard,
  FileCheck,
  Download,
  Upload,
  Eye,
  ShieldAlert,
  CheckCircle,
  FileText,
  User,
  Shield
} from 'lucide-react';
import { fetchUsers } from '../../api';
import { generatePrintableIdCard, generatePrintableAuthLetter } from '../../utils/printReport';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

export default function IDAndDocs() {
  const { currentRole, currentUserId } = useApp();
  const [users, setUsers] = useState([]);

  useEffect(() => {
    let isMounted = true;
    async function load() {
      try {
        const data = await fetchUsers();
        if (isMounted && Array.isArray(data)) setUsers(data);
      } catch (err) {
        if (isMounted) setUsers([]);
      }
    }
    load();
    return () => { isMounted = false; };
  }, []);
  
  const [activeTab, setActiveTab] = useState('field_executives');
  const [showIdModal, setShowIdModal] = useState(false);
  const [showUploadAuthModal, setShowUploadAuthModal] = useState(false);
  const [showUploadIdModal, setShowUploadIdModal] = useState(false);
  
  const [authLetterName, setAuthLetterName] = useState('HDFC_Auth_Letter_2026.pdf');
  const [authLetterDate, setAuthLetterDate] = useState('2026-05-15');
  const [idCardTemplateName, setIdCardTemplateName] = useState('Official_ID_Template_v2.png');
  
  // Simulated file uploads
  const [newAuthFile, setNewAuthFile] = useState('');
  const [newIdTemplateFile, setNewIdTemplateFile] = useState('');

  // Filter roles
  const fieldExecutives = users.filter(u => u.role === 'field_executive');
  const vendors = users.filter(u => u.role === 'vendor');

  const [selectedFeId, setSelectedFeId] = useState('');
  const [selectedVendorId, setSelectedVendorId] = useState('');

  // Track user to preview in modal
  const [displayIdUser, setDisplayIdUser] = useState(null);

  const selectedFeUser = users.find(u => u.id === selectedFeId) || fieldExecutives[0] || null;
  const selectedVendorUser = users.find(u => u.id === selectedVendorId) || vendors[0] || null;

  const handleUploadAuth = () => {
    if (!newAuthFile) {
      toast.error('Please select a file to upload.');
      return;
    }
    const fileName = newAuthFile.split('\\').pop() || 'Auth_Letter.pdf';
    setAuthLetterName(fileName);
    setAuthLetterDate(new Date().toISOString().split('T')[0]);
    setShowUploadAuthModal(false);
    setNewAuthFile('');
    toast.success('Authorization letter uploaded and active successfully!');
  };

  const handleUploadId = () => {
    if (!newIdTemplateFile) {
      toast.error('Please select a file to upload.');
      return;
    }
    const fileName = newIdTemplateFile.split('\\').pop() || 'ID_Template.png';
    setIdCardTemplateName(fileName);
    setShowUploadIdModal(false);
    setNewIdTemplateFile('');
    toast.success('ID Card Template updated successfully!');
  };

  const handleDownloadFile = (fileName, type = 'auth') => {
    if (type === 'id') {
      generatePrintableIdCard(selectedFeUser);
    } else {
      generatePrintableAuthLetter(selectedFeUser);
    }
    toast.success(`Generated official printable document: ${fileName}`);
  };

  // Secure path access check
  if (currentRole !== ROLES.ADMIN) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8 bg-white border border-slate-200 rounded-3xl shadow-sm">
        <div className="w-16 h-16 rounded-full bg-rose-50 flex items-center justify-center text-rose-500 mb-4 animate-bounce">
          <ShieldAlert className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-bold text-slate-805">Access Denied</h2>
        <p className="text-sm text-slate-400 mt-2 max-w-md leading-relaxed">
          This portal is reserved strictly for administrative personnel. Please view your official identity card and authorization letter inside your Profile modal in the top-right header menu.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in text-slate-800" data-testid="id-auth-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">ID & Authorization Workspace</h1>
          <p className="text-sm text-slate-500 mt-1">Official credentials, identity cards and client-issued field visitation letters</p>
        </div>
        
        {/* Admin upload options */}
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => setShowUploadAuthModal(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold gap-2 shadow-lg shadow-emerald-500/10 text-xs py-2 px-4 rounded-xl h-auto"
          >
            <Upload className="w-4 h-4" />
            Upload Authorization
          </Button>
          <Button
            onClick={() => setShowUploadIdModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-2 shadow-lg shadow-blue-500/10 text-xs py-2 px-4 rounded-xl h-auto"
          >
            <Upload className="w-4 h-4" />
            Upload ID Template
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white border border-slate-200 p-1.5 w-full flex mb-6 rounded-2xl shadow-xs">
          <TabsTrigger 
            value="field_executives" 
            className="flex-1 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 font-bold gap-2 py-2.5 rounded-xl transition-all"
          >
            <IdCard className="w-4 h-4" />
            Field Executives ({fieldExecutives.length})
          </TabsTrigger>
          <TabsTrigger 
            value="vendors" 
            className="flex-1 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-600 font-bold gap-2 py-2.5 rounded-xl transition-all"
          >
            <Shield className="w-4 h-4" />
            Vendors ({vendors.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="field_executives" className="space-y-6 mt-0">
          {/* FE Selector Card */}
          <div className="crm-card p-4 bg-slate-50 border border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-4 rounded-2xl">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
                <User className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-805">Field Executive List</p>
                <p className="text-xs text-slate-400">Select an executive to view or download credentials</p>
              </div>
            </div>
            <select
              value={selectedFeId}
              onChange={(e) => setSelectedFeId(e.target.value)}
              className="rounded-xl bg-white border border-slate-200 px-4 py-2.5 text-xs font-bold text-slate-800 outline-none cursor-pointer w-full md:w-72 shadow-xs focus:ring-2 focus:ring-blue-500/25"
            >
              {fieldExecutives.map(fe => (
                <option key={fe.id} value={fe.id}>{fe.name} ({fe.id})</option>
              ))}
            </select>
          </div>

          {selectedFeUser && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* ID Card */}
              <div className="crm-card p-6 bg-white border border-slate-200 relative overflow-hidden shadow-sm flex flex-col justify-between min-h-[420px] rounded-3xl">
                <div className="absolute top-0 right-0 w-36 h-36 bg-blue-600/5 rounded-bl-full pointer-events-none"></div>
                
                <div className="space-y-5">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 border border-blue-100 shadow-sm">
                      <IdCard className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 leading-tight">Official System ID Card</h3>
                      <p className="text-xs text-slate-400 mt-0.5">Validated field executive identity card for on-site audits</p>
                    </div>
                  </div>

                  <div className="bg-slate-50 border border-slate-150 p-5 rounded-2xl space-y-3.5">
                    <div className="flex justify-between items-center text-xs border-b border-slate-200/50 pb-2.5">
                      <span className="text-slate-500 font-medium">Holder Name:</span>
                      <span className="font-bold text-slate-805">{selectedFeUser.name}</span>
                    </div>
                    <div className="flex justify-between items-center text-xs border-b border-slate-200/50 pb-2.5">
                      <span className="text-slate-500 font-medium">Employee ID:</span>
                      <span className="font-bold text-slate-805">{selectedFeUser.id}</span>
                    </div>
                    <div className="flex justify-between items-center text-xs border-b border-slate-200/50 pb-2.5">
                      <span className="text-slate-500 font-medium">Active Role:</span>
                      <span className="font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded text-[10px] uppercase">Field Executive</span>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500 font-medium">Template File:</span>
                      <span className="font-semibold text-slate-600 truncate max-w-[150px]">{idCardTemplateName}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mt-6">
                  <Button
                    onClick={() => {
                      setDisplayIdUser(selectedFeUser);
                      setShowIdModal(true);
                    }}
                    className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold gap-1.5 text-xs py-2.5 shadow-xs rounded-xl border border-slate-200"
                  >
                    <Eye className="w-4 h-4" />
                    View ID Card
                  </Button>
                  <Button
                    onClick={() => handleDownloadFile(`${selectedFeUser.name.replace(' ', '_')}_ID.pdf`, 'id')}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-1.5 text-xs py-2.5 shadow-xs rounded-xl"
                  >
                    <Download className="w-4 h-4" />
                    Download ID
                  </Button>
                </div>
              </div>

              {/* Authorization Letter */}
              <div className="crm-card p-6 bg-white border border-slate-200 relative overflow-hidden shadow-sm flex flex-col justify-between min-h-[420px] rounded-3xl">
                <div className="absolute top-0 right-0 w-36 h-36 bg-emerald-600/5 rounded-bl-full pointer-events-none"></div>

                <div className="space-y-5">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600 border border-emerald-100 shadow-sm">
                      <FileCheck className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 leading-tight">Verification Authority Letter</h3>
                      <p className="text-xs text-slate-400 mt-0.5">Client-approved official document authorizing physical field checks</p>
                    </div>
                  </div>

                  <div className="bg-slate-50 border border-slate-150 p-5 rounded-2xl space-y-3.5">
                    <div className="flex justify-between items-center text-xs border-b border-slate-200/50 pb-2.5">
                      <span className="text-slate-500 font-medium">Document Type:</span>
                      <span className="font-bold text-slate-805 flex items-center gap-1.5">
                        <FileText className="w-4 h-4 text-slate-400" />
                        Authorization Letter
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-xs border-b border-slate-200/50 pb-2.5">
                      <span className="text-slate-500 font-medium">Active File:</span>
                      <span className="font-bold text-slate-850 truncate max-w-[180px]">{authLetterName}</span>
                    </div>
                    <div className="flex justify-between items-center text-xs border-b border-slate-200/50 pb-2.5">
                      <span className="text-slate-500 font-medium">Published Date:</span>
                      <span className="font-bold text-slate-700">{authLetterDate}</span>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500 font-medium">Security Status:</span>
                      <span className="font-bold text-emerald-600 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        Verified Active
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-[11px] text-slate-400 italic">
                    * Field Executives are advised to carry a printed copy or offline cached PDF copy of this letter on active field duty.
                  </p>
                </div>

                <Button
                  onClick={() => handleDownloadFile(authLetterName)}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold gap-1.5 text-xs py-2.5 mt-6 shadow-xs rounded-xl"
                >
                  <Download className="w-4 h-4" />
                  Download Authorization Letter
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="vendors" className="space-y-6 mt-0">
          {/* Vendor Selector Card */}
          <div className="crm-card p-4 bg-slate-50 border border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-4 rounded-2xl">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
                <User className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-805">Vendor List</p>
                <p className="text-xs text-slate-400">Select a vendor to manage or download official credentials</p>
              </div>
            </div>
            <select
              value={selectedVendorId}
              onChange={(e) => setSelectedVendorId(e.target.value)}
              className="rounded-xl bg-white border border-slate-200 px-4 py-2.5 text-xs font-bold text-slate-800 outline-none cursor-pointer w-full md:w-72 shadow-xs focus:ring-2 focus:ring-emerald-500/25"
            >
              {vendors.map(v => (
                <option key={v.id} value={v.id}>{v.name} ({v.id})</option>
              ))}
            </select>
          </div>

          {selectedVendorUser && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* ID Card (Deactivated for Vendors) */}
              <div className="crm-card p-6 bg-slate-50/70 border border-slate-200 rounded-3xl flex flex-col items-center justify-center text-center min-h-[420px] relative overflow-hidden">
                <div className="absolute top-0 right-0 w-36 h-36 bg-slate-200/25 rounded-bl-full pointer-events-none"></div>
                <div className="w-16 h-16 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center mb-4 text-slate-400">
                  <Shield className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-bold text-slate-800">ID Card Locked</h3>
                <p className="text-xs text-slate-400 mt-2 max-w-xs leading-relaxed">
                  System ID Cards are exclusively generated for active field executives on duty. Identification cards are deactivated for Vendor profiles.
                </p>
                <div className="mt-6 w-full max-w-[200px] border border-dashed border-slate-300 p-2.5 rounded-xl bg-white/60 text-[10px] font-extrabold text-slate-450 uppercase tracking-widest">
                  Not Applicable
                </div>
              </div>

              {/* Authorization Letter (Active for Vendors) */}
              <div className="crm-card p-6 bg-white border border-slate-200 relative overflow-hidden shadow-sm flex flex-col justify-between min-h-[420px] rounded-3xl">
                <div className="absolute top-0 right-0 w-36 h-36 bg-emerald-600/5 rounded-bl-full pointer-events-none"></div>

                <div className="space-y-5">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600 border border-emerald-100 shadow-sm">
                      <FileCheck className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 leading-tight">Verification Authority Letter</h3>
                      <p className="text-xs text-slate-400 mt-0.5">Client-approved official document authorizing physical field checks</p>
                    </div>
                  </div>

                  <div className="bg-slate-50 border border-slate-150 p-5 rounded-2xl space-y-3.5">
                    <div className="flex justify-between items-center text-xs border-b border-slate-200/50 pb-2.5">
                      <span className="text-slate-500 font-medium">Document Type:</span>
                      <span className="font-bold text-slate-805 flex items-center gap-1.5">
                        <FileText className="w-4 h-4 text-slate-400" />
                        Authorization Letter
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-xs border-b border-slate-200/50 pb-2.5">
                      <span className="text-slate-500 font-medium">Active File:</span>
                      <span className="font-bold text-slate-850 truncate max-w-[180px]">{authLetterName}</span>
                    </div>
                    <div className="flex justify-between items-center text-xs border-b border-slate-200/50 pb-2.5">
                      <span className="text-slate-500 font-medium">Published Date:</span>
                      <span className="font-bold text-slate-700">{authLetterDate}</span>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500 font-medium">Security Status:</span>
                      <span className="font-bold text-emerald-600 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        Verified Active
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-[11px] text-slate-400 italic">
                    * Vendors can view and download active letters for their business records.
                  </p>
                </div>

                <Button
                  onClick={() => handleDownloadFile(authLetterName)}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold gap-1.5 text-xs py-2.5 mt-6 shadow-xs rounded-xl"
                >
                  <Download className="w-4 h-4" />
                  Download Authorization Letter
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ID CARD PREVIEW MODAL */}
      <Dialog open={showIdModal} onOpenChange={setShowIdModal}>
        <DialogContent className="bg-slate-100 border-slate-200 justify-center flex flex-col items-center max-w-sm">
          <DialogHeader>
            <DialogTitle className="sr-only">Official Identity Card Preview</DialogTitle>
          </DialogHeader>
          
          <div className="w-[280px] h-[420px] bg-white rounded-xl shadow-2xl relative overflow-hidden border border-slate-200 mt-2">
            <div className="h-24 bg-blue-600 w-full absolute top-0 left-0" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 75%)' }}></div>

            <div className="relative z-10 flex flex-col items-center pt-8 px-6">
              <div className="w-full flex justify-between items-start mb-1">
                <span className="text-white font-extrabold text-sm tracking-wider">AD Ventures</span>
                <span className="text-white/80 text-[9px] uppercase font-bold tracking-widest">Verify ID</span>
              </div>

              <Avatar className="w-24 h-24 border-4 border-white shadow-md mb-3 bg-slate-100 mt-2">
                <AvatarImage src={displayIdUser.avatar} className="object-cover" />
                <AvatarFallback className="text-2xl text-slate-400">{displayIdUser.name?.split(' ').map(n => n[0]).join('') || 'U'}</AvatarFallback>
              </Avatar>

              <h2 className="text-lg font-bold text-slate-800 text-center leading-tight">{displayIdUser.name}</h2>
              <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mt-1 mb-4">Field Executive</p>

              <div className="w-full space-y-2 text-xs">
                <div className="bg-slate-50 p-2 rounded border border-slate-100 flex justify-between">
                  <span className="text-slate-450 uppercase text-[9px] font-bold">Emp ID</span>
                  <span className="font-bold text-slate-800">{displayIdUser.id}</span>
                </div>
                <div className="bg-slate-50 p-2 rounded border border-slate-100 flex justify-between">
                  <span className="text-slate-450 uppercase text-[9px] font-bold">Blood Group</span>
                  <span className="font-bold text-red-600">O+</span>
                </div>
              </div>

              <div className="absolute bottom-0 left-0 w-full p-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                <div className="w-20 h-6 bg-slate-200 rounded flex items-center justify-center text-[7px] tracking-wider text-slate-450">||| |||| || |||</div>
                <span className="text-[8px] text-slate-450">Valid: 12/2026</span>
              </div>
            </div>
          </div>

          <DialogFooter className="w-full mt-4 flex justify-center">
            <Button
              onClick={() => handleDownloadFile(`${displayIdUser.name.replace(' ', '_')}_ID.pdf`)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-1.5"
            >
              <Download className="w-4 h-4" /> Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* UPLOAD AUTH LETTER MODAL */}
      <Dialog open={showUploadAuthModal} onOpenChange={setShowUploadAuthModal}>
        <DialogContent className="bg-white border-slate-200 max-w-md text-slate-800">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Upload New Authorization Letter</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-3">
            <p className="text-xs text-slate-500">
              Replace the active field visitation authority document. FEs and Vendors in your hierarchy will automatically fetch the new file.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="authFileInput">Select PDF Document</Label>
              <Input
                id="authFileInput"
                type="file"
                accept=".pdf"
                className="bg-slate-50 border-slate-200 text-xs"
                onChange={(e) => setNewAuthFile(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadAuthModal(false)} className="border-slate-200 text-slate-700 hover:bg-slate-50">
              Cancel
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleUploadAuth}>
              Publish Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* UPLOAD ID TEMPLATE MODAL */}
      <Dialog open={showUploadIdModal} onOpenChange={setShowUploadIdModal}>
        <DialogContent className="bg-white border-slate-200 max-w-md text-slate-800">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Upload ID Card Template</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-3">
            <p className="text-xs text-slate-500">
              Update the baseline layout/design template for all generated identity cards in the system.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="idFileInput">Select Template Image (PNG/JPG)</Label>
              <Input
                id="idFileInput"
                type="file"
                accept=".png,.jpg,.jpeg"
                className="bg-slate-50 border-slate-200 text-xs"
                onChange={(e) => setNewIdTemplateFile(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadIdModal(false)} className="border-slate-200 text-slate-700 hover:bg-slate-50">
              Cancel
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleUploadId}>
              Update Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
